/**
 * ============================================================================
 * EL-ARO
 * COMPONENT: Hero
 * FILE: assets/elaro-hero.js
 * ============================================================================
 */

(() => {

    'use strict';


    /*==========================================================================
      HERO
    ==========================================================================*/

    class ElaroHero {

        constructor(root) {

            this.root = root;

            this.productTilt =
                root.querySelector(
                    '[data-hero-product-tilt]'
                );

            this.animationFrame = null;

            this.currentX = 0;
            this.currentY = 0;

            this.targetX = 0;
            this.targetY = 0;

            this.isHovering = false;

            this.init();

        }


        /*======================================================================
          INIT
        ======================================================================*/

        init() {

            this.setupReveal();

            this.setupMagneticProduct();

        }


        /*======================================================================
          REVEAL
        ======================================================================*/

        setupReveal() {

            const content =
                this.root.querySelector(
                    '.elaro-hero__content'
                );

            const media =
                this.root.querySelector(
                    '.elaro-hero__media'
                );


            if (content) {

                content.classList.add(
                    'elaro-hero__content--ready'
                );

            }


            if (media) {

                media.classList.add(
                    'elaro-hero__media--ready'
                );

            }


            if (
                window.matchMedia(
                    '(prefers-reduced-motion: reduce)'
                ).matches
            ) {

                this.root.classList.add(
                    'is-visible'
                );

                return;

            }


            if (!window.IntersectionObserver) {

                this.root.classList.add(
                    'is-visible'
                );

                return;

            }


            const observer =
                new IntersectionObserver(
                    entries => {

                        entries.forEach(
                            entry => {

                                if (
                                    entry.isIntersecting
                                ) {

                                    this.root.classList.add(
                                        'is-visible'
                                    );

                                    observer.disconnect();

                                }

                            }
                        );

                    },
                    {
                        threshold: 0.15
                    }
                );


            observer.observe(this.root);

        }


        /*======================================================================
          MAGNETIC PRODUCT
        ======================================================================*/

        setupMagneticProduct() {

            const element =
                this.productTilt;


            if (!element) {

                console.warn(
                    'EL ARO: Magnetic product element not found.'
                );

                return;

            }


            /*
             * Disable the old 3D system completely.
             */

            element.style.removeProperty(
                'perspective'
            );

            element.style.removeProperty(
                'transform-style'
            );

            element.style.removeProperty(
                'transform-origin'
            );

            element.style.removeProperty(
                'will-change'
            );


            /*
             * Only enable magnetic movement
             * on devices that actually support hover.
             */

            if (
                !window.matchMedia(
                    '(hover: hover) and (pointer: fine)'
                ).matches
            ) {

                return;

            }


            element.addEventListener(
                'mouseenter',
                () => {

                    this.isHovering = true;

                }
            );


            element.addEventListener(
                'mousemove',
                event => {

                    const rect =
                        element.getBoundingClientRect();


                    if (
                        rect.width <= 0 ||
                        rect.height <= 0
                    ) {

                        return;

                    }


                    /*
                     * Mouse position:
                     *
                     * left  = -1
                     * center = 0
                     * right = +1
                     */

                    const mouseX =
                        (
                            event.clientX -
                            rect.left
                        ) /
                        rect.width;


                    const mouseY =
                        (
                            event.clientY -
                            rect.top
                        ) /
                        rect.height;


                    const normalizedX =
                        (mouseX - 0.5) * 2;


                    const normalizedY =
                        (mouseY - 0.5) * 2;


                    /*
                     * Maximum magnetic movement.
                     *
                     * Keep this small.
                     */

                    const MAX_MOVE_X = 8;

                    const MAX_MOVE_Y = 8;


                    this.targetX =
                        normalizedX *
                        MAX_MOVE_X;


                    this.targetY =
                        normalizedY *
                        MAX_MOVE_Y;


                    this.startMagneticAnimation();

                }
            );


            element.addEventListener(
                'mouseleave',
                () => {

                    this.isHovering = false;

                    this.targetX = 0;

                    this.targetY = 0;

                    this.startMagneticAnimation();

                }
            );


            console.log(
                'EL ARO: Magnetic product effect initialized.'
            );

        }


        /*======================================================================
          MAGNETIC ANIMATION
        ======================================================================*/

        startMagneticAnimation() {

            if (this.animationFrame) {

                return;

            }


            const animate = () => {

                /*
                 * Smooth spring interpolation.
                 */

                this.currentX +=
                    (
                        this.targetX -
                        this.currentX
                    ) * 0.12;


                this.currentY +=
                    (
                        this.targetY -
                        this.currentY
                    ) * 0.12;


                /*
                 * IMPORTANT:
                 *
                 * ONLY TRANSLATION.
                 *
                 * No rotate.
                 * No scale.
                 * No perspective.
                 */

                this.productTilt.style.transform =
                    `translate3d(${this.currentX}px, ${this.currentY}px, 0)`;


                const differenceX =
                    Math.abs(
                        this.targetX -
                        this.currentX
                    );


                const differenceY =
                    Math.abs(
                        this.targetY -
                        this.currentY
                    );


                const settled =
                    differenceX < 0.05 &&
                    differenceY < 0.05;


                if (
                    this.isHovering ||
                    !settled
                ) {

                    this.animationFrame =
                        requestAnimationFrame(
                            animate
                        );

                } else {

                    this.animationFrame = null;

                }

            };


            this.animationFrame =
                requestAnimationFrame(
                    animate
                );

        }


        /*======================================================================
          DESTROY
        ======================================================================*/

        destroy() {

            if (this.animationFrame) {

                cancelAnimationFrame(
                    this.animationFrame
                );

                this.animationFrame = null;

            }


            if (this.productTilt) {

                this.productTilt.style.transform =
                    '';

            }

        }

    }


    /*==========================================================================
      INITIALIZE
    ==========================================================================*/

    const initialize = () => {

        document
            .querySelectorAll(
                '[data-elaro-hero]'
            )
            .forEach(
                root => {

                    if (
                        root.__elaroHeroInstance
                    ) {

                        return;

                    }


                    root.__elaroHeroInstance =
                        new ElaroHero(root);

                }
            );

    };


    /*==========================================================================
      DOM READY
    ==========================================================================*/

    if (
        document.readyState === 'loading'
    ) {

        document.addEventListener(
            'DOMContentLoaded',
            initialize
        );

    } else {

        initialize();

    }


    /*==========================================================================
      SHOPIFY THEME EDITOR
    ==========================================================================*/

    document.addEventListener(
        'shopify:section:load',
        event => {

            const root =
                event.target.querySelector(
                    '[data-elaro-hero]'
                );


            if (!root) {

                return;

            }


            if (
                root.__elaroHeroInstance
            ) {

                root.__elaroHeroInstance.destroy();

            }


            root.__elaroHeroInstance =
                new ElaroHero(root);

        }
    );


    document.addEventListener(
        'shopify:section:unload',
        event => {

            const root =
                event.target.querySelector(
                    '[data-elaro-hero]'
                );


            if (
                root &&
                root.__elaroHeroInstance
            ) {

                root.__elaroHeroInstance.destroy();

                delete root.__elaroHeroInstance;

            }

        }
    );

})();